
import React, { useState } from 'react';
import { Heart, TrendingUp, Users, BookOpen, Send, MapPin, User, Mail, Phone, Briefcase, FileText } from 'lucide-react';

const Careers: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    message: ''
  });

  const perks = [
    {
      title: 'Rémunération attractive',
      text: 'Salaire fixe compétitif + primes de performance non plafonnées.',
      icon: <TrendingUp className="text-emerald-600" />
    },
    {
      title: 'Évolution réelle',
      text: '100% de nos managers sont issus de la promotion interne.',
      icon: <TrendingUp className="text-copper-600" />
    },
    {
      title: 'Bien-être & Sport',
      text: 'Activités sportives, challenges internes et espace détente moderne.',
      icon: <Heart className="text-rose-500" />
    },
    {
      title: 'Formation continue',
      text: 'Accédez à la PrimeConnect Academy pour monter en compétence.',
      icon: <BookOpen className="text-amber-600" />
    }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const mailtoLink = `mailto:contact@primeconnect.biz?subject=Candidature - ${formData.position} - ${formData.name}&body=Nom: ${formData.name}%0D%0AEmail: ${formData.email}%0D%0ATéléphone: ${formData.phone}%0D%0APoste: ${formData.position}%0D%0A%0D%0AMotivation:%0D%0A${formData.message}`;
    window.location.href = mailtoLink;
  };

  return (
    <div className="pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="mb-24 text-center max-w-3xl mx-auto">
          <div className="inline-block bg-copper-100 text-copper-600 px-5 py-2 rounded-full text-xs font-bold uppercase mb-8 tracking-[0.2em] border border-copper-200">
            #TeamPrimeConnect
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 font-heading mb-8">Construisons votre carrière ensemble.</h1>
          <p className="text-xl text-slate-600 leading-relaxed font-medium">
            Ici, on ne recrute pas des numéros. Nous cherchons des talents passionnés par l'humain et l'innovation technologique.
          </p>
        </header>

        <section className="mb-32">
          <h2 className="text-3xl font-bold text-slate-900 font-heading text-center mb-20">Pourquoi vous allez aimer travailler ici ?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
             {perks.map((perk, i) => (
               <div key={i} className="bg-white border border-sand-300 p-10 rounded-[2.5rem] hover:border-copper-600 transition-all group shadow-sm hover:shadow-xl hover:shadow-copper-600/5">
                  <div className="w-14 h-14 bg-sand-100 rounded-2xl flex items-center justify-center mb-8 border border-sand-200 group-hover:scale-110 transition-transform shadow-sm">
                    {perk.icon}
                  </div>
                  <h4 className="text-xl font-bold text-slate-900 mb-4 font-heading">{perk.title}</h4>
                  <p className="text-slate-500 text-sm leading-relaxed font-medium">{perk.text}</p>
               </div>
             ))}
          </div>
        </section>

        {/* Job Application Form Section */}
        <section id="apply-form" className="mb-32">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="space-y-8">
              <h2 className="text-4xl font-bold text-slate-900 font-heading">Rejoignez l'aventure.</h2>
              <p className="text-lg text-slate-600 leading-relaxed font-medium">
                Vous avez du talent, de l'énergie et l'envie de grandir dans un environnement technologique et humain ? Remplissez ce formulaire pour nous envoyer votre candidature directe.
              </p>
              
              <div className="space-y-6 pt-6">
                <div className="flex items-center space-x-4 text-slate-700 font-semibold">
                  <div className="w-10 h-10 rounded-full bg-prime-blue/10 flex items-center justify-center text-prime-blue">
                    <MapPin size={20} />
                  </div>
                  <span>Hubs : Douala, Paris, Montréal</span>
                </div>
                <div className="flex items-center space-x-4 text-slate-700 font-semibold">
                  <div className="w-10 h-10 rounded-full bg-prime-green/10 flex items-center justify-center text-prime-green">
                    <Users size={20} />
                  </div>
                  <span>+250 collaborateurs passionnés</span>
                </div>
              </div>
            </div>

            <div className="bg-white border border-sand-300 p-8 md:p-12 rounded-[3.5rem] shadow-2xl shadow-sand-300/30">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center">
                      <User className="w-3 h-3 mr-2" /> Nom complet
                    </label>
                    <input 
                      required
                      type="text" 
                      placeholder="Jean Dupont"
                      className="w-full bg-sand-50 border border-sand-200 rounded-2xl px-5 py-4 focus:border-copper-600 outline-none transition-all text-slate-900 font-medium"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center">
                      <Mail className="w-3 h-3 mr-2" /> Email
                    </label>
                    <input 
                      required
                      type="email" 
                      placeholder="jean.dupont@email.com"
                      className="w-full bg-sand-50 border border-sand-200 rounded-2xl px-5 py-4 focus:border-copper-600 outline-none transition-all text-slate-900 font-medium"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center">
                      <Phone className="w-3 h-3 mr-2" /> Téléphone
                    </label>
                    <input 
                      required
                      type="tel" 
                      placeholder="+237 / +33"
                      className="w-full bg-sand-50 border border-sand-200 rounded-2xl px-5 py-4 focus:border-copper-600 outline-none transition-all text-slate-900 font-medium"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center">
                      <Briefcase className="w-3 h-3 mr-2" /> Poste souhaité
                    </label>
                    <input 
                      required
                      type="text" 
                      placeholder="ex: Conseiller Client, Manager..."
                      className="w-full bg-sand-50 border border-sand-200 rounded-2xl px-5 py-4 focus:border-copper-600 outline-none transition-all text-slate-900 font-medium"
                      value={formData.position}
                      onChange={(e) => setFormData({...formData, position: e.target.value})}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center">
                    <FileText className="w-3 h-3 mr-2" /> Motivation / Message
                  </label>
                  <textarea 
                    required
                    rows={4}
                    placeholder="Pourquoi voulez-vous rejoindre PRIMECONNECT ?"
                    className="w-full bg-sand-50 border border-sand-200 rounded-2xl px-5 py-4 focus:border-copper-600 outline-none transition-all text-slate-900 font-medium"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                  ></textarea>
                </div>

                <button 
                  type="submit"
                  className="w-full bg-copper-600 hover:bg-copper-700 text-white py-5 rounded-2xl font-bold text-lg transition-all flex items-center justify-center shadow-xl shadow-copper-600/20"
                >
                  Envoyer ma candidature
                  <Send className="ml-3 w-5 h-5" />
                </button>
                <p className="text-slate-400 text-[10px] text-center font-bold uppercase tracking-widest">
                  Réponse garantie sous 48h par nos équipes RH
                </p>
              </form>
            </div>
          </div>
        </section>

        <div className="relative group overflow-hidden rounded-[4rem] border-8 border-white shadow-2xl">
           <img 
            src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&q=80&w=1200" 
            alt="Team Working" 
            className="w-full h-[600px] object-cover opacity-80 group-hover:scale-105 transition-transform duration-1000"
           />
           <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent flex flex-col items-center justify-end pb-20 px-8 text-center">
              <h2 className="text-5xl font-bold text-white font-heading mb-8">Prêt à franchir le cap ?</h2>
              <p className="text-sand-100/80 max-w-xl mb-12 text-lg font-medium">
                Envoyez-nous votre candidature spontanée ou répondez à nos offres. Nos équipes RH vous répondent sous 48h.
              </p>
              <div className="flex flex-col items-center space-y-4">
                <button 
                  onClick={() => document.getElementById('apply-form')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-copper-600 hover:bg-copper-700 text-white px-12 py-6 rounded-full font-bold text-xl transition-all flex items-center shadow-2xl shadow-copper-600/40 transform hover:-translate-y-1"
                >
                  Postuler via le formulaire
                  <Send className="ml-3 w-6 h-6" />
                </button>
                <p className="text-white/40 text-xs font-bold uppercase tracking-widest">Ou envoyez directement à : contact@primeconnect.biz</p>
              </div>
           </div>
        </div>
      </div>
    </div>
  );
};

export default Careers;
